import updateCabinetVisualizer from './updateCabinetVisualizer';
import { saveCabinetDesign } from '../../../api/design';
import logNewRelicCustom from '../../../utils/logNewRelicCustom';
import { NEW_RELIC_EVENTS } from '../../../constants';

/**
 * Apply collection mapping (SKU remapping) to all designs in the current project.
 * This is used when a user selects a different collection to update all cabinet products.
 *
 * The function:
 * 1. Remaps all product SKUs using the itemIdMap from the selected collection
 * 2. Updates DesignStyleChoiceid on all designs
 * 3. Saves all updated designs to backend
 * 4. Updates local store state
 *
 * @param {Function} set - Zustand set function
 * @param {Function} get - Zustand get function
 * @param {string} host - API host URL
 * @param {Object} selectedCollection - Collection object from matching API response
 * @param {Object} selectedCollection.itemIdMap - Map of oldSku -> newSku
 * @param {string} selectedCollection.designStyleChoiceId - New style choice ID
 * @param {string} selectedCollection.collectionId - Collection identifier
 * @param {Object} options - Optional configuration
 * @param {boolean} options.saveToBackend - Whether to save designs to backend (default: true)
 * @returns {Promise<{success: boolean, error?: string, updatedDesignIds?: string[]}>}
 */
export default async function applyCollectionMapping(set, get, host, selectedCollection, options = {}) {
  const { saveToBackend = true } = options;
  const state = get().cabinetVisualizer;

  try {
    const { itemIdMap, designStyleChoiceId, collectionId, itemId } = selectedCollection;

    if (!itemIdMap || Object.keys(itemIdMap).length === 0) {
      logNewRelicCustom(NEW_RELIC_EVENTS.APPLY_COLLECTION_MAPPING_ERROR, {
        reason: 'empty itemIdMap',
        collectionId
      });
      return { success: false, error: 'No SKU mappings provided' };
    }

    const { designs, designIds, styleChoiceId: currentStyleChoiceId } = get().cabinetVisualizer;

    if (!designs || Object.keys(designs).length === 0) {
      return { success: false, error: 'No designs to update' };
    }

    const newStyleChoiceId = parseInt(designStyleChoiceId, 10);
    const updatedDesigns = {};
    const updatedDesignIds = [];
    let totalProductsRemapped = 0;

    // Process each design and remap SKUs
    Object.entries(designs).forEach(([designId, design]) => {
      // Skip temp designs without products
      if (designId.startsWith('temp-') && (!design.Products || design.Products.length === 0)) {
        updatedDesigns[designId] = design;
        return;
      }

      // Remap products using itemIdMap
      const remappedProducts = (design.Products || []).map((product) => {
        const currentSku = String(product.Sku);

        // Map original SKU to new collection's SKU
        const newSku = itemIdMap[currentSku];
        if (newSku && newSku !== currentSku) {
          totalProductsRemapped += 1;

          return {
            ...product,
            Sku: newSku,
          };
        }
        return product;
      });

      const cartSkus = state.designs[designId]?.Cart || [];
      const toeKickItemIds = new Set(
        state.toeKickMetadata?.childItemsLookup?.map((tk) => tk.itemId.toString()) || []
      );

      const updatedCartSkus = cartSkus.filter((cartSku) => {
        const skuString = cartSku.Sku.toString();
        const isToeKick = toeKickItemIds.has(skuString);

        return !isToeKick;
      });

      // Remap products using itemIdMap
      let totalCartProductsRemapped = 0;
      const remappedCartProducts = (updatedCartSkus).map((item) => {
        const currentSku = String(item.Sku);

        // Map original SKU to new collection's SKU
        const newSku = itemIdMap[currentSku];

        if (newSku && newSku !== currentSku) {
          totalCartProductsRemapped += 1;

          return {
            ...item,
            Sku: newSku,
          };
        }

        return item;
      });

      updatedDesigns[designId] = {
        ...design,
        Products: remappedProducts,
        DesignStyleChoiceid: newStyleChoiceId,
        Cart: remappedCartProducts,
        CollectionId: itemId || collectionId // Use itemId if available, fallback to collectionId
      };
      updatedDesignIds.push(designId);

    });

    // CRITICAL FIX: Recalculate styleDesignIds to include ALL designs now that they all have
    // the new DesignStyleChoiceid
    // Since we just updated ALL designs to have the new styleChoiceId, styleDesignIds should be ALL design IDs
    const newStyleDesignIds = Object.keys(updatedDesigns).filter((id) => {
      const design = updatedDesigns[id];
      return design && parseInt(design.DesignStyleChoiceid, 10) === newStyleChoiceId;
    });

    // CRITICAL FIX: Update the project object in localStorage with new styleChoiceId
    // This prevents stale data from being loaded when initializeWithUrlWorkflow runs
    const currentProjectId = get().cabinetVisualizer.currentProjectId;
    const projects = get().cabinetVisualizer.projects || {};
    let updatedProjects = { ...projects };

    if (currentProjectId && projects[currentProjectId]) {
      updatedProjects[currentProjectId] = {
        ...projects[currentProjectId],
        styleChoiceId: newStyleChoiceId,
        lastModified: new Date().toISOString()
      };

    }
    // Update local store state immediately for responsive UI
    set(
      updateCabinetVisualizer(set, get, {
        designs: updatedDesigns,
        styleDesignIds: newStyleDesignIds, // Use recalculated styleDesignIds that match new collection
        styleChoiceId: newStyleChoiceId,
        projects: updatedProjects, // Update projects so localStorage gets updated via persist
        userSelectedStyleChoice: true,
        collectionChangeTriggered: true,
        skuDrawer: {
          modalAssortment: {},
          superSkuProducts: [],
          selectedItemId: null,
          selectedParentId: null,
        }
      }),
      false,
      { type: 'cabinetVisualizer/applyCollectionMapping', collectionId }
    );
    // Update the URL with the new DesignStyleChoiceid and CollectionId to keep URL in sync with store
    // This doesn't trigger re-renders but ensures the URL reflects the current state
    if (typeof window !== 'undefined') {
      const currentUrl = new URL(window.location);
      currentUrl.searchParams.set('DesignStyleChoiceid', newStyleChoiceId);

      // Replace the collection ID in the path (the numeric segment before the query string)
      // Example: /cabinet-shop-your-space/332697259?... -> /cabinet-shop-your-space/NEW_ID?...
      if (itemId || collectionId) {
        const newCollectionId = itemId || collectionId;
        const currentPath = currentUrl.pathname;
        // Remove any existing numeric ID segments and append the new collection ID
        const basePathSegments = currentPath.split('/').filter((segment) => segment && !/^[0-9]+$/.test(segment));
        currentUrl.pathname = `/${basePathSegments.join('/')}/${newCollectionId}`;
      }

      window.history.replaceState({}, '', currentUrl.toString());
    }

    // Save all updated designs to backend
    if (saveToBackend) {
      const savePromises = updatedDesignIds
        .filter((designId) => !designId.startsWith('temp-'))
        .map(async (designId) => {
          try {
            await saveCabinetDesign(host, updatedDesigns[designId]);
            return { designId, success: true };
          } catch (error) {
            return { designId, success: false, error: error.message };
          }
        });

      const saveResults = await Promise.all(savePromises);
      const failedSaves = saveResults.filter((result) => !result.success);

      if (failedSaves.length > 0) {
        logNewRelicCustom(NEW_RELIC_EVENTS.APPLY_COLLECTION_MAPPING_ERROR, {
          reason: 'partial save failure',
          collectionId,
          failedDesignIds: failedSaves.map((fr) => fr.designId)
        });
        // Continue anyway - local state is updated
      }
    }

    // Log success
    logNewRelicCustom(NEW_RELIC_EVENTS.APPLY_COLLECTION_MAPPING_SUCCESS, {
      collectionId,
      newStyleChoiceId,
      previousStyleChoiceId: currentStyleChoiceId,
      designsUpdated: updatedDesignIds.length,
      productsRemapped: totalProductsRemapped
    });

    return {
      success: true,
      updatedDesignIds,
      productsRemapped: totalProductsRemapped
    };
  } catch (error) {
    logNewRelicCustom(NEW_RELIC_EVENTS.APPLY_COLLECTION_MAPPING_ERROR, {
      reason: 'unexpected error',
      errorMessage: error?.message || 'Unknown error',
      collectionId: selectedCollection?.collectionId
    });
    return { success: false, error: error.message || 'Unknown error applying collection mapping' };
  }
}

/**
 * Get statistics about how many products will be remapped
 * Useful for showing user confirmation before applying
 *
 * @param {Object} designs - Current designs object
 * @param {Object} itemIdMap - SKU mapping from selected collection
 * @returns {Object} Stats object { totalProducts, mappableProducts, unmappableProducts }
 */
export function getRemappingStats(designs, itemIdMap) {
  if (!designs || !itemIdMap) {
    return { totalProducts: 0, mappableProducts: 0, unmappableProducts: 0 };
  }

  let totalProducts = 0;
  let mappableProducts = 0;

  Object.values(designs).forEach((design) => {
    (design.Products || []).forEach((product) => {
      totalProducts += 1;
      const oldSku = String(product.Sku);
      if (itemIdMap[oldSku]) {
        mappableProducts += 1;
      }
    });
  });

  return {
    totalProducts,
    mappableProducts,
    unmappableProducts: totalProducts - mappableProducts
  };
}
